import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api/client';
import { createCustomDashboardTab, uploadCustomDashboard } from '@/lib/api/custom-dashboard';
import { extractDashboardBundle } from '@/lib/custom-dashboard/dashboard-bundle';
import { readFileBytes, readFileText } from '@/lib/custom-dashboard/read-file';
import {
  DATA_COMPRESSED_MAX_BYTES,
  DATA_MAX_BYTES,
  DETAILS_COMPRESSED_MAX_BYTES,
  DETAILS_MAX_BYTES,
  HTML_COMPRESSED_MAX_BYTES,
  HTML_MAX_BYTES,
  extractMetaName,
  formatByteSize,
  validateDashboardHtml,
  validateDataJson,
  validateDetailsFile,
  validateTabName,
} from '@/lib/custom-dashboard/upload-validation';

/**
 * アップロード先。新規タブはタブ名の入力欄を表示し、`group` があればそのグループの末尾に、null ならグループ未指定に作る。
 */
export type UploadDialogTarget =
  | { kind: 'existing'; tabId: string; tabName: string }
  | { kind: 'new'; group: { groupId: string; name: string } | null };

export interface UploadDialogProps {
  accountId: string;
  authHeaders: Record<string, string>;
  target: UploadDialogTarget;
  /** アップロード成功後の「今すぐプレビュー」。呼び出し元がプレビューダイアログを開く。 */
  onPreview: (tabId: string, revision: number) => void;
  onClose: () => void;
}

interface SelectedFile {
  file: File;
  text: string;
}

// 展開時・圧縮時の上限はどちらも必須。data.json は圧縮率が中身次第なので、実データでの目安を添える。
const HTML_LIMIT_HINT = `${formatByteSize(HTML_MAX_BYTES)} 以下の HTML（送信時に自動で圧縮。圧縮後 ${formatByteSize(HTML_COMPRESSED_MAX_BYTES)} 以下が必要）`;
const DATA_LIMIT_HINT = `${formatByteSize(DATA_MAX_BYTES)} 以下の JSON（送信時に自動で圧縮。圧縮後 ${formatByteSize(DATA_COMPRESSED_MAX_BYTES)} 以下が必要で、数値中心のデータなら ${formatByteSize(DATA_MAX_BYTES)} まで収まります）`;
const DETAILS_LIMIT_HINT = `${formatByteSize(DETAILS_MAX_BYTES)} 以下のテキスト（送信時に自動で圧縮。圧縮後 ${formatByteSize(DETAILS_COMPRESSED_MAX_BYTES)} 以下が必要）`;

/** dashboard.html + data.json と任意の details.txt を検証してアップロードするダイアログ。 */
export function UploadDialog({
  accountId,
  authHeaders,
  target,
  onPreview,
  onClose,
}: UploadDialogProps) {
  const queryClient = useQueryClient();
  const [newTabName, setNewTabName] = useState('');
  // data.json から prefill した後にユーザーが編集した場合、prefill で上書きしない。
  const [isTabNameEdited, setIsTabNameEdited] = useState(false);
  const [dashboard, setDashboard] = useState<SelectedFile | null>(null);
  const [data, setData] = useState<SelectedFile | null>(null);
  const [details, setDetails] = useState<File | null>(null);
  /** 取り込み済み zip のファイル名。取り込み結果の表示にのみ使う。 */
  const [bundleName, setBundleName] = useState<string | null>(null);
  const [bundleErrors, setBundleErrors] = useState<string[]>([]);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploaded, setUploaded] = useState<{ tabId: string; revision: number } | null>(null);

  const isNewTab = target.kind === 'new';

  const handleFileChange =
    (setter: (value: SelectedFile | null) => void) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setServerErrors([]);
      setBundleErrors([]);
      const file = event.target.files?.[0];
      if (!file) {
        setter(null);
        return;
      }
      readFileText(file)
        .then((text) => setter({ file, text }))
        .catch(() => toast.error(`${file.name} の読み込みに失敗しました`));
    };

  const applyData = (value: SelectedFile | null) => {
    setData(value);
    // タブ名は sazae 側が正だが、成果物に name があれば初期値として拾っておく。
    if (isNewTab && value && !isTabNameEdited) {
      const metaName = extractMetaName(value.text);
      if (metaName) setNewTabName(metaName);
    }
  };

  const handleDataChange = handleFileChange(applyData);

  /** zip を展開して 3 項目に割り当てる。送信は従来どおり multipart 3 パート。 */
  const handleBundleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setServerErrors([]);
    setBundleErrors([]);
    const file = event.target.files?.[0];
    // 直したあとに同じ zip を選び直しても change が発火するよう、選択状態は都度空へ戻す。
    // （取り込み結果は下のサマリが正で、input 側に選択を残す必要が無い）
    event.target.value = '';
    if (!file) return;
    readFileBytes(file)
      .then((bytes) => {
        const { bundle, errors } = extractDashboardBundle(bytes);
        if (bundle === null) {
          // 取り込めなかった zip では各項目を変更しない（前回の取り込み内容を壊さない）。
          setBundleErrors(errors);
          return;
        }
        setDashboard(bundle.dashboard);
        applyData(bundle.data);
        // zip の内容で 3 項目を置き換える（details を含まない zip なら未選択に戻す）。
        setDetails(bundle.details);
        setBundleName(file.name);
      })
      .catch(() => toast.error(`${file.name} の読み込みに失敗しました`));
  };

  const tabNameErrors = useMemo(
    () => (isNewTab ? validateTabName(newTabName) : []),
    [isNewTab, newTabName],
  );
  const clientErrors = useMemo(
    () => [
      ...bundleErrors,
      // 未入力のタブ名のエラーは入力が始まるまで表示しない（送信は tabNameErrors で無効化済み）
      ...(isNewTab && newTabName !== '' ? tabNameErrors : []),
      ...(dashboard ? validateDashboardHtml(dashboard.text) : []),
      ...(data ? validateDataJson(data.text, accountId) : []),
      ...(details ? validateDetailsFile(details) : []),
    ],
    [bundleErrors, isNewTab, newTabName, tabNameErrors, dashboard, data, details, accountId],
  );

  const canSubmit =
    dashboard !== null &&
    data !== null &&
    tabNameErrors.length === 0 &&
    clientErrors.length === 0 &&
    !isUploading;

  const handleUpload = async () => {
    if (dashboard === null || data === null) return;
    setIsUploading(true);
    setServerErrors([]);
    try {
      const files = {
        dashboard: dashboard.file,
        data: data.file,
        ...(details ? { details } : {}),
      };
      const result =
        target.kind === 'new'
          ? await createCustomDashboardTab(
              accountId,
              {
                name: newTabName.trim(),
                ...(target.group ? { groupId: target.group.groupId } : {}),
              },
              files,
              authHeaders,
            )
          : await uploadCustomDashboard(accountId, target.tabId, files, authHeaders);
      // アップロードは revision を作るだけで表示（activeRevision）は変わらないため、
      // display 系 query（custom-dashboard-tabs など）の invalidate は不要。
      await queryClient.invalidateQueries({
        queryKey: ['custom-dashboard-all-tabs', accountId],
      });
      toast.success(
        `revision ${result.revision} を作成しました。プレビューを確認してから公開してください`,
      );
      // 閉じずに成功状態へ切り替え、「今すぐプレビュー」で公開フローへ誘導する。
      setUploaded({ tabId: result.tabId, revision: result.revision });
    } catch (error) {
      // 失敗（S3 put 以降）でも新規タブは最初の tx で作成済み
      // （upload_service.rs）のため、一覧が stale にならないよう失敗時も再取得する。
      await queryClient.invalidateQueries({
        queryKey: ['custom-dashboard-all-tabs', accountId],
      });
      if (error instanceof ApiError && error.status === 400) {
        // BE は複数の検証エラーを改行連結した message で返す（upload.rs）。
        // トーストに畳み込まずダイアログ内に一覧表示する。
        setServerErrors(error.message.split('\n'));
      } else {
        toast.error(error instanceof Error ? error.message : 'アップロードに失敗しました');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isUploading) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>ダッシュボードをアップロード</DialogTitle>
          <DialogDescription>
            {target.kind === 'existing'
              ? `タブ「${target.tabName}」に新しい revision を作成します。`
              : target.group
                ? `タブグループ「${target.group.name}」に新しいタブを作成します。`
                : '新しいタブを「グループ未指定」に作成します。タブグループに入れると閲覧画面に表示されます。'}
          </DialogDescription>
        </DialogHeader>
        {uploaded !== null ? (
          <>
            <p className="text-sm">
              revision {uploaded.revision}{' '}
              を作成しました。プレビューで描画を確認してから公開してください。
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                閉じる
              </Button>
              <Button onClick={() => onPreview(uploaded.tabId, uploaded.revision)}>
                今すぐプレビュー
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-4">
              {isNewTab && (
                <div className="space-y-2">
                  <Label htmlFor="upload-tab-name">タブ名</Label>
                  <Input
                    id="upload-tab-name"
                    value={newTabName}
                    placeholder="例: 週次KPI"
                    onChange={(event) => {
                      setServerErrors([]);
                      setIsTabNameEdited(true);
                      setNewTabName(event.target.value);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    タブバーに表示される名前です。作成後もいつでも変更できます。
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="upload-bundle-zip">まとめてアップロード（zip・任意）</Label>
                <Input
                  id="upload-bundle-zip"
                  type="file"
                  accept=".zip,application/zip"
                  onChange={handleBundleChange}
                />
                <p className="text-xs text-muted-foreground">
                  zip 内の dashboard.html / data.json（任意で
                  details.txt）を下の各項目に取り込みます。
                </p>
                {(bundleName !== null || bundleErrors.length > 0) && (
                  <div className="rounded-md border bg-muted/40 p-3 text-xs">
                    <p>
                      {bundleErrors.length > 0
                        ? 'zip を取り込めませんでした。各項目は変更していません（現在の内容）。'
                        : `zip「${bundleName}」から取り込みました。個別に差し替えることもできます。`}
                    </p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4">
                      <li>dashboard.html: {dashboard?.file.name ?? '未取り込み'}</li>
                      <li>data.json: {data?.file.name ?? '未取り込み'}</li>
                      <li>details.txt: {details?.name ?? '無し'}</li>
                    </ul>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="upload-dashboard-html">dashboard.html</Label>
                <Input
                  id="upload-dashboard-html"
                  type="file"
                  accept=".html"
                  aria-describedby="upload-dashboard-html-hint"
                  onChange={handleFileChange(setDashboard)}
                />
                <p id="upload-dashboard-html-hint" className="text-xs text-muted-foreground">
                  {HTML_LIMIT_HINT}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="upload-data-json">data.json</Label>
                <Input
                  id="upload-data-json"
                  type="file"
                  accept=".json"
                  aria-describedby="upload-data-json-hint"
                  onChange={handleDataChange}
                />
                <p id="upload-data-json-hint" className="text-xs text-muted-foreground">
                  {DATA_LIMIT_HINT}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="upload-details-txt">details.txt（任意）</Label>
                <Input
                  id="upload-details-txt"
                  type="file"
                  accept=".txt,text/plain"
                  aria-describedby="upload-details-txt-hint"
                  onChange={(event) => {
                    setServerErrors([]);
                    setBundleErrors([]);
                    setDetails(event.target.files?.[0] ?? null);
                  }}
                />
                <p id="upload-details-txt-hint" className="text-xs text-muted-foreground">
                  {DETAILS_LIMIT_HINT}
                </p>
              </div>
              {clientErrors.length > 0 && <ErrorList errors={clientErrors} />}
              {serverErrors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-red-800">
                    サーバー側の検証で拒否されました。内容を修正して再度アップロードしてください:
                  </p>
                  <ErrorList errors={serverErrors} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isUploading}>
                キャンセル
              </Button>
              <Button onClick={() => void handleUpload()} disabled={!canSubmit}>
                {isUploading ? 'アップロード中...' : 'アップロード'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ErrorList({ errors }: { errors: string[] }) {
  return (
    <ul className="list-disc space-y-1 rounded-md border border-red-300 bg-red-50 py-3 pl-8 pr-4 text-sm text-red-800">
      {errors.map((error) => (
        <li key={error}>{error}</li>
      ))}
    </ul>
  );
}
